import { FC } from "react";
import styled from "@emotion/styled";
import { MDSText } from "@/mds/components/text/text";
import { MDSTextVariant, MDSTextWeight } from "@/mds/components/text/text/text.types";

/**
 * Slack-style "New" row marking where the owner's unread messages begin. Held
 * for the surface's lifetime once shown — the anchor is latched at open, so
 * marking the session seen does not pull the row out from under the reader.
 *
 * Retiring dissolves the row rather than removing it: the box keeps its height
 * so the messages below never reflow.
 */
export const UnreadDividerSessionItemRenderer: FC<{ isRetired?: boolean }> = ({ isRetired }) => (
  <StyledUnreadDivider
    role="separator"
    aria-label="New messages"
    aria-hidden={isRetired || undefined}
    $isRetired={isRetired ?? false}
  >
    <StyledUnreadLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.Medium}>
      New
    </StyledUnreadLabel>
    <StyledUnreadRule />
  </StyledUnreadDivider>
);

const UNREAD_DIVIDER_FADE_MS = 400;

const StyledUnreadDivider = styled.div<{ $isRetired: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: ${({ theme }) => theme.spacing["2"]} 0;
  opacity: ${({ $isRetired }) => ($isRetired ? 0 : 1)};
  transition: opacity ${UNREAD_DIVIDER_FADE_MS}ms ease-out;
  pointer-events: ${({ $isRetired }) => ($isRetired ? "none" : "auto")};
`;

const StyledUnreadLabel = styled(MDSText)`
  color: ${({ theme }) => theme.colors.icon.brand};
  flex-shrink: 0;
`;

const StyledUnreadRule = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.icon.brand};
`;
